/**
 * Parser für FlashLive throw-by-throw-Daten (Darts).
 *
 * Semantik der BLOCKS (live verifiziert 2026-07-12 European Tour 10 Finale
 * UND 2026-08-14 NZ Darts Masters):
 * - Ein Block ≈ ein Leg — ABER NUR IM ENDZUSTAND. FlashLive liefert den
 *   Leg-Split VERZÖGERT (~30–90s): Beim Leg-Start hängen die Würfe des neuen
 *   Legs erst IM BLOCK DES VORGÄNGER-LEGS (SCORE_HISTORY resettet mittendrin
 *   auf 501), erst später wird in saubere Blocks umstrukturiert (Block-Anzahl
 *   1→2→3 live beobachtet, Indizes verschieben sich!). Ein Block kann also
 *   MEHRERE Legs enthalten → In-Block-Splitting ist PFLICHT.
 * - SCORE_HISTORY endet NIEMALS bei 0 — der Checkout-Wurf wird nicht
 *   angehängt. Der Checkout ist der letzte Rest des Leg-Gewinners (≤ 170).
 * - MAIN_SCORE des LETZTEN Blocks wird SOFORT beim Leg-Ende aktualisiert
 *   (vor dem History-Resplit) = Match-Stand nach dem zuletzt BEENDETEN Leg;
 *   IS_CHANGED/SERVICE_LOST beschreiben dieses zuletzt beendete Leg — NICHT
 *   das laufende. → Ob das letzte Segment läuft, entscheidet der Vergleich
 *   der MAIN-Summe mit (Vorgänger-Stand + fertige Segmente im Block).
 * - MAIN_SCORE eingefrorener Blöcke = Match-Stand NACH dem jeweiligen Leg.
 *   Gewinner robust über das Delta zum Vorgänger-Block ableiten.
 * - SERVICE_PARTICIPANT_TYPE = Anwerfer des ERSTEN Legs im Block; weitere
 *   Segmente wechseln den Anwurf (Darts-Standard: Legs alternieren).
 * - TYPE:"TEXT_SPEC"-Einträge sind Highlight-Marker (z.B. "140", "180") und
 *   beziehen sich auf die direkt davor gelistete Aufnahme.
 * - BLOCKS kann im Leg-Übergang (und sporadisch mitten im Leg) 30–90s komplett
 *   leer sein — Aufrufer müssen dann den letzten Stand behalten.
 */

export interface ScoreboardVisit {
  /** 1 = Heim, 2 = Auswärts */
  team: 1 | 2;
  /** Geworfene Punkte dieser Aufnahme (null, wenn nicht ableitbar) */
  thrown: number | null;
  /** Restpunkte nach der Aufnahme */
  remaining: number;
  /** Highlight-Marker der Aufnahme (z.B. "140", "180") */
  highlight: string | null;
}

export interface ScoreboardLeg {
  /** 1-basierte Leg-Nummer im Match */
  legNumber: number;
  isFinished: boolean;
  /** 1 = Heim, 2 = Auswärts, null = läuft noch / unbekannt */
  winner: 1 | 2 | null;
  /** Checkout-Wert des Gewinners (letzter Rest ≤ 170), null wenn unbekannt */
  checkout: number | null;
  /** Anwerfer des Legs: 1 | 2 | null */
  starter: 1 | 2 | null;
  visits: ScoreboardVisit[];
  /** Letzter bekannter Rest je Spieler (laufendes Leg: aktueller Spielstand) */
  homeRemaining: number;
  awayRemaining: number;
  /** Match-Stand (Legs) nach diesem Leg — nur bei fertigen Legs gesetzt */
  matchScore: { home: number; away: number } | null;
}

export interface ParsedScoreboard {
  legs: ScoreboardLeg[];
  /** Das laufende Leg (letztes, nicht beendetes) — null wenn keins läuft */
  currentLeg: ScoreboardLeg | null;
}

const toInt = (v: any): number | null => {
  const n = parseInt(String(v ?? ''), 10);
  return isNaN(n) ? null : n;
};

const parseMainScore = (block: any): { home: number; away: number; changedTeam: 1 | 2 | null } => {
  let home = 0;
  let away = 0;
  let changedTeam: 1 | 2 | null = null;
  const mainScore = Array.isArray(block?.MAIN_SCORE) ? block.MAIN_SCORE : [];
  for (const ms of mainScore) {
    const order = toInt(ms?.TEAM_ORDER);
    const value = toInt(ms?.VALUE) ?? 0;
    if (order === 1) home = value;
    if (order === 2) away = value;
    if (toInt(ms?.IS_CHANGED) === 1 && (order === 1 || order === 2)) {
      changedTeam = order as 1 | 2;
    }
  }
  return { home, away, changedTeam };
};

/** Ein Block-Segment = die Aufnahmen EINES Legs (nach In-Block-Splitting) */
interface Segment {
  visits: ScoreboardVisit[];
  homeRemaining: number;
  awayRemaining: number;
}

/**
 * Zerlegt die SCORE_HISTORY eines Blocks in Leg-Segmente. Ein neues Segment
 * beginnt, wenn der Rest eines Spielers nach OBEN auf Leg-Start-Niveau
 * (≥ 400) springt — dann hat innerhalb des Blocks ein neues Leg begonnen.
 */
const splitBlockIntoSegments = (block: any): Segment[] => {
  const segments: Segment[] = [];
  let current: Segment = { visits: [], homeRemaining: 501, awayRemaining: 501 };
  const history = Array.isArray(block?.SCORE_HISTORY) ? block.SCORE_HISTORY : [];

  const closeSegment = () => {
    segments.push(current);
    current = { visits: [], homeRemaining: 501, awayRemaining: 501 };
  };

  for (const entry of history) {
    if (entry?.TYPE === 'SCORE') {
      const items = Array.isArray(entry.ITEMS) ? entry.ITEMS : [];
      for (const item of items) {
        if (toInt(item?.IS_CHANGED) !== 1) continue;
        const order = toInt(item?.TEAM_ORDER);
        const remaining = toInt(item?.VALUE);
        if ((order !== 1 && order !== 2) || remaining === null) continue;

        const prevRemaining = order === 1 ? current.homeRemaining : current.awayRemaining;
        const diff = prevRemaining - remaining;

        // Leg-Neustart im Block: Der Rest kann im X01 NIE steigen — ein
        // deutlicher Sprung nach oben ist immer ein neues Leg. >= 400 fängt
        // normale Starts, der 120er-Sprung auch 180-Starts (501→321, live
        // beobachtet NZ Masters Leg 5); kleine Sprünge bleiben als
        // Datenkorrektur im Leg (thrown wird dort ohnehin null).
        if (diff < 0 && (remaining >= 400 || remaining - prevRemaining > 120) && current.visits.length > 0) {
          closeSegment();
          if (remaining >= 501) {
            // Reiner Reset-Marker (Spieler zurück auf 501) — kein Wurf
            continue;
          }
          // Erste Aufnahme des neuen Legs (z.B. 501 → 405)
          const thrown = 501 - remaining;
          current.visits.push({
            team: order as 1 | 2,
            thrown: thrown >= 0 && thrown <= 180 ? thrown : null,
            remaining,
            highlight: null,
          });
          if (order === 1) current.homeRemaining = remaining;
          else current.awayRemaining = remaining;
          continue;
        }

        // Plausible Aufnahme: 0-180 Punkte
        const thrown = diff >= 0 && diff <= 180 ? diff : null;
        current.visits.push({ team: order as 1 | 2, thrown, remaining, highlight: null });
        if (order === 1) current.homeRemaining = remaining;
        else current.awayRemaining = remaining;
      }
    } else if (entry?.VALUE != null && current.visits.length > 0) {
      // TEXT_SPEC (z.B. "140", "180") gehört zur direkt vorherigen Aufnahme
      current.visits[current.visits.length - 1].highlight = String(entry.VALUE);
    }
  }
  segments.push(current);
  return segments.filter((s) => s.visits.length > 0);
};

export function parseThrowByThrow(blocks: any[], matchFinished: boolean): ParsedScoreboard {
  const legs: ScoreboardLeg[] = [];
  if (!Array.isArray(blocks) || blocks.length === 0) {
    return { legs, currentLeg: null };
  }

  // Match-Stand VOR dem ersten Block ist immer 0:0
  let prevMain = { home: 0, away: 0 };

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    const hasNextBlock = i < blocks.length - 1;
    const main = parseMainScore(block);
    const starterRaw = toInt(block?.SERVICE_PARTICIPANT_TYPE);
    const blockStarter: 1 | 2 | null = starterRaw === 1 || starterRaw === 2 ? starterRaw : null;

    const segments = splitBlockIntoSegments(block);
    if (segments.length === 0) {
      prevMain = { home: main.home, away: main.away };
      continue;
    }

    // Läuft das LETZTE Segment noch? MAIN zählt nur BEENDETE Legs:
    // Stand-Summe = Vorgänger-Summe + fertige Segmente. Liegt MAIN darüber,
    // ist auch das letzte Segment beendet (MAIN wird sofort beim Leg-Ende
    // aktualisiert, noch vor dem History-Resplit).
    const prevSum = prevMain.home + prevMain.away;
    const mainSum = main.home + main.away;
    const lastSegmentFinished =
      hasNextBlock || matchFinished || mainSum > prevSum + (segments.length - 1);

    // Gewinner-Zuordnung: MAIN-Delta über den ganzen Block; eindeutig nur,
    // wenn im Block genau EIN Leg für genau EIN Team dazukam. Sonst nur das
    // LETZTE fertige Segment über IS_CHANGED (beschreibt das zuletzt
    // beendete Leg), frühere Segmente bleiben unbekannt (transienter Zustand).
    const deltaHome = main.home - prevMain.home;
    const deltaAway = main.away - prevMain.away;
    const completedCount = lastSegmentFinished ? segments.length : segments.length - 1;
    const lastCompletedIndex = lastSegmentFinished ? segments.length - 1 : segments.length - 2;

    for (let s = 0; s < segments.length; s++) {
      const segment = segments[s];
      const isFinished = s < segments.length - 1 || lastSegmentFinished;

      let winner: 1 | 2 | null = null;
      if (isFinished && s === lastCompletedIndex) {
        if (deltaHome + deltaAway === 1 && completedCount === 1) {
          winner = deltaHome === 1 ? 1 : 2;
        } else {
          winner = main.changedTeam;
        }
      }

      let checkout: number | null = null;
      if (isFinished && winner !== null) {
        const rest = winner === 1 ? segment.homeRemaining : segment.awayRemaining;
        checkout = rest > 0 && rest <= 170 ? rest : null;
      }

      // Leg-Nummer aus dem Match-Stand ableiten (Block-Indizes verschieben
      // sich serverseitig): letztes fertiges Segment = MAIN-Summe, frühere
      // zählen rückwärts, laufendes Segment = MAIN-Summe + 1
      let legNumber: number;
      if (isFinished) {
        legNumber = mainSum - (lastCompletedIndex - s);
      } else {
        legNumber = mainSum + 1;
      }
      if (legNumber <= 0) legNumber = legs.length + 1;

      // Anwurf: erstes Segment = Block-Angabe, weitere Legs alternieren
      const starter: 1 | 2 | null =
        blockStarter === null ? null : ((((blockStarter - 1 + s) % 2) + 1) as 1 | 2);

      legs.push({
        legNumber,
        isFinished,
        winner,
        checkout,
        starter,
        visits: segment.visits,
        homeRemaining: segment.homeRemaining,
        awayRemaining: segment.awayRemaining,
        matchScore: isFinished && s === lastCompletedIndex
          ? { home: main.home, away: main.away }
          : null,
      });
    }

    prevMain = { home: main.home, away: main.away };
  }

  const last = legs[legs.length - 1];
  let currentLeg: ScoreboardLeg | null = last && !last.isFinished ? last : null;

  // Leg-Ende ohne ersten Wurf des nächsten Legs (Marcel 2026-10-18): FlashLive
  // schreibt den Checkout nie in die Historie, das neue Leg taucht erst mit
  // seiner ersten Aufnahme auf. Bis dahin blieben 46/81 stehen. Deshalb gibt es
  // sofort ein leeres laufendes Leg mit 501/501 (Anwurf wechselt) – genau wie
  // auf der Bühnen-Anzeige. Nicht bei beendeten Matches.
  if (!matchFinished && last && last.isFinished) {
    const next: ScoreboardLeg = {
      legNumber: last.legNumber + 1,
      isFinished: false,
      winner: null,
      checkout: null,
      starter: last.starter === null ? null : last.starter === 1 ? 2 : 1,
      visits: [],
      homeRemaining: 501,
      awayRemaining: 501,
      matchScore: null,
    };
    legs.push(next);
    currentLeg = next;
  }
  return { legs, currentLeg };
}
